/**
 * CharacterSelectPage
 *
 * Two-step character / specialty selection shown every time a learner
 * enters a domain from the Dashboard.
 *
 * Step 1: Select or create a character class (role)
 * Step 2: Select a specialty (skipped if the domain has no specialties)
 *
 * On completion, calls participantAPI.activateCharacter() then navigates
 * to /domain/:domainId.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { participantAPI, marketplaceAPI } from '../services/api';
import AccessDenied from '../components/AccessDenied';
import CatalogCard from '../../../shared/components/CatalogCard';
import { confirm } from '../../../shared/utils/overlays';


const STEP_CHARACTER = 1;
const STEP_SPECIALTY = 2;

function CharacterCard({ character, selected, onSelect, onDelete }) {
  const xpFooter = character.xp_in_domain > 0
    ? { right: `${character.xp_in_domain} XP · Lvl ${character.level_in_domain || 1}` }
    : undefined;

  return (
    <CatalogCard
      title={character.display_name}
      description={character.character_class?.replace(/_/g, ' ')}
      icon={character.avatar_url || character.icon || '👤'}
      badges={character.personality_type ? [{ label: character.personality_type }] : []}
      footer={xpFooter}
      selected={selected}
      actions={[
        { label: 'Select', variant: 'primary', onClick: () => onSelect(character) },
        {
          label: '🗑', variant: 'danger',
          onClick: async () => {
            const ok = await confirm({
              title: 'Delete character?',
              message: `Delete "${character.display_name}"? This cannot be undone.`,
              confirmLabel: 'Delete',
              danger: true,
            });
            if (ok) onDelete(character);
          },
        },
      ]}
      onClick={() => onSelect(character)}
    />
  );
}

function SpecialtyCard({ specialty, selected, unlocked, onSelect }) {
  return (
    <div
      className={`csp-spec-card ${selected ? 'selected' : ''} ${!unlocked ? 'locked' : ''}`}
      onClick={() => unlocked && onSelect(specialty)}
    >
      <div className="csp-spec-name">{specialty.display_name}</div>
      {!unlocked && <div className="csp-locked-badge">🔒 Locked</div>}
      {selected && <div className="csp-selected-tick">✓</div>}
    </div>
  );
}

function NewCharacterForm({ domainId, learnerId, characterClasses, onCreated }) {
  // characterClasses is [{id, label, description, icon, default}, ...]
  const [cls, setCls] = useState(characterClasses[0]?.id || '');
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!name.trim()) { setError('Please enter a name.'); return; }
    setCreating(true);
    setError(null);
    try {
      const { data } = await participantAPI.createCharacter(learnerId, {
        domain_id: domainId,
        character_class: cls,
        display_name: name.trim(),
      });
      onCreated(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="csp-new-char-form">
      <h4>Create New Character</h4>
      <select value={cls} onChange={(e) => setCls(e.target.value)}>
        {characterClasses.map((c) => (
          <option key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ''}{c.label}</option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Your name (e.g. Alex)..."
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
      />
      {error && <div className="csp-error">{error}</div>}
      <button onClick={handleCreate} disabled={creating}>
        {creating ? 'Creating...' : 'Create Character'}
      </button>
    </div>
  );
}

export default function CharacterSelectPage() {
  const { domainId } = useParams();
  const { learner, setActiveCharacter, setActiveSpecialty } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP_CHARACTER);
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [characterClasses, setCharacterClasses] = useState([]);
  const [showNewCharForm, setShowNewCharForm] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!learner?.learner_id) return;
    const learnerId = learner.learner_id;
    const token = localStorage.getItem('scenario_os_token');

    Promise.allSettled([
      participantAPI.listCharacters(learnerId, domainId),
      participantAPI.getSubscriptions(learnerId, domainId),
      participantAPI.getAvailableClasses(domainId),
    ]).then(([charsResult, subsResult, classesResult]) => {
      // Characters — graceful: start with none
      const existingChars = charsResult.status === 'fulfilled'
        ? (charsResult.value.data.all_characters || charsResult.value.data.characters || [])
        : [];
      setCharacters(existingChars);

      // Subscription
      const subs = subsResult.status === 'fulfilled'
        ? (subsResult.value.data.subscriptions || [])
        : [];
      setSubscription(subs.find((s) => s.domain_id === domainId) || null);

      // Character classes from participant API (coach-configured or pack defaults)
      const classesData = classesResult.status === 'fulfilled' ? classesResult.value.data : null;
      const classes = classesData?.character_classes || [];
      setCharacterClasses(classes);
      setSpecialties([]);

      // Auto-select: if only one class AND learner already has a character, auto-activate
      if (classesData?.auto_select && existingChars.length > 0) {
        // Auto-activate the existing character and proceed
        const char = existingChars[0];
        participantAPI.activateCharacter(learnerId, char.character_id, domainId, null)
          .then(() => {
            setActiveCharacter(char);
            navigate(`/domain/${domainId}`, { state: { character_id: char.character_id } });
          })
          .catch(() => setLoading(false));
        return;
      }

      // Auto-select: single class, no character yet — auto-create with default name
      if (classesData?.auto_select && existingChars.length === 0 && classes.length === 1) {
        const cls = classes[0];
        participantAPI.createCharacter(learnerId, {
          domain_id: domainId,
          character_class: cls.id,
          display_name: cls.label,
        }).then((res) => {
          const newChar = res.data;
          return participantAPI.activateCharacter(learnerId, newChar.character_id, domainId, null)
            .then(() => {
              setActiveCharacter(newChar);
              navigate(`/domain/${domainId}`, { state: { character_id: newChar.character_id } });
            });
        }).catch(() => setLoading(false));
        return;
      }

      setLoading(false);
    });
  }, [learner, domainId, navigate, setActiveCharacter]);

  const handleSelectCharacter = useCallback((char) => {
    setSelectedChar(char);
  }, []);

  const handleDeleteCharacter = useCallback(async (char) => {
    try {
      await participantAPI.deleteCharacter(learner.learner_id, char.character_id, domainId);
      setCharacters((prev) => prev.filter((c) => c.character_id !== char.character_id));
      if (selectedChar?.character_id === char.character_id) setSelectedChar(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete character.');
    }
  }, [learner, domainId, selectedChar]);

  const handleContinue = () => {
    if (!selectedChar) { setError('Please select a character.'); return; }
    if (specialties.length === 0) {
      // Skip step 2
      handleActivate(selectedChar, null);
    } else {
      setStep(STEP_SPECIALTY);
    }
  };

  const handleActivate = async (char, specialty) => {
    setActivating(true);
    setError(null);
    try {
      await participantAPI.activateCharacter(
        learner.learner_id,
        char.character_id,
        domainId,
        specialty?.specialty_id || null,
      );
      setActiveCharacter(char);
      setActiveSpecialty(specialty || null);
      navigate(`/domain/${domainId}`, {
        state: { character_id: char.character_id, specialty_id: specialty?.specialty_id || null },
      });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setActivating(false);
    }
  };

  const isSpecialtyUnlocked = (spec) => {
    if (!spec.unlock_requirements || Object.keys(spec.unlock_requirements).length === 0) return true;
    if (subscription?.unlocked_specialties?.includes(spec.specialty_id)) return true;
    if (!selectedChar) return false;
    const skills = selectedChar.skill_tree || {};
    return Object.entries(spec.unlock_requirements).every(([skill, minVal]) => (skills[skill] || 0) >= minVal);
  };

  if (loading) {
    return (
      <div className="csp-loading">
        <div className="csp-spinner" />
        <p>Loading your characters...</p>
      </div>
    );
  }

  // Enrollment check — learner.enrolled_domains is set at login from the learner profile
  const enrolledDomains = learner?.enrolled_domains || [];
  if (!enrolledDomains.includes(domainId)) {
    return <AccessDenied domainId={domainId} onBack={() => navigate('/')} />;
  }

  return (
    <div className="csp-page">
      <div className="csp-header">
        <button className="csp-back-btn" onClick={() => navigate('/')}>← Back</button>
        <div className="csp-breadcrumb">
          <span className={step === STEP_CHARACTER ? 'active' : 'done'}>1. Choose Character</span>
          {specialties.length > 0 && (
            <>
              <span className="csp-bc-sep">›</span>
              <span className={step === STEP_SPECIALTY ? 'active' : ''}>2. Choose Specialty</span>
            </>
          )}
        </div>
      </div>

      {error && <div className="csp-error-banner">{error}</div>}

      {/* Step 1: Character Selection */}
      {step === STEP_CHARACTER && (
        <div className="csp-step">
          <h2>Choose your character for <strong>{domainId}</strong></h2>
          <p className="csp-subtitle">
            Each character maintains independent progress and skill progression.
          </p>

          <div className="csp-char-grid">
            {characters.map((c) => (
              <CharacterCard
                key={c.character_id}
                character={c}
                selected={selectedChar?.character_id === c.character_id}
                onSelect={handleSelectCharacter}
                onDelete={handleDeleteCharacter}
              />
            ))}

            <div
              className="csp-char-card new-char"
              onClick={() => setShowNewCharForm((v) => !v)}
            >
              <div className="csp-char-icon">＋</div>
              <div className="csp-char-name">New Character</div>
            </div>
          </div>

          {showNewCharForm && characterClasses.length > 0 && (
            <NewCharacterForm
              domainId={domainId}
              learnerId={learner.learner_id}
              characterClasses={characterClasses}
              onCreated={(newChar) => {
                setCharacters((prev) => [...prev, newChar]);
                setSelectedChar(newChar);
                setShowNewCharForm(false);
              }}
            />
          )}

          <button
            className="csp-continue-btn"
            onClick={handleContinue}
            disabled={!selectedChar || activating}
          >
            {activating ? 'Activating...' : specialties.length > 0 ? 'Continue →' : 'Enter Domain →'}
          </button>
        </div>
      )}

      {/* Step 2: Specialty Selection */}
      {step === STEP_SPECIALTY && (
        <div className="csp-step">
          <h2>Choose your specialty</h2>
          <p className="csp-subtitle">
            Playing as <strong>{selectedChar?.display_name}</strong>.
            Each specialty has its own skill progression.
          </p>

          <div className="csp-spec-grid">
            {specialties.map((spec) => {
              const unlocked = isSpecialtyUnlocked(spec);
              return (
                <SpecialtyCard
                  key={spec.specialty_id}
                  specialty={spec}
                  selected={selectedSpecialty?.specialty_id === spec.specialty_id}
                  unlocked={unlocked}
                  onSelect={setSelectedSpecialty}
                />
              );
            })}
          </div>

          <div className="csp-step2-actions">
            <button className="csp-back-step-btn" onClick={() => setStep(STEP_CHARACTER)}>← Back</button>
            <button
              className="csp-continue-btn"
              onClick={() => handleActivate(selectedChar, selectedSpecialty)}
              disabled={!selectedSpecialty || activating}
            >
              {activating ? 'Activating...' : 'Enter Domain →'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .csp-page { min-height: 100vh; background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%); color: #e2e8f0; font-family: system-ui, sans-serif; padding: 2rem; }
        .csp-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; color: #e2e8f0; gap: 1rem; }
        .csp-spinner { width: 40px; height: 40px; border: 3px solid rgba(255,255,255,0.1); border-top-color: #6366f1; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .csp-header { display: flex; align-items: center; gap: 1.5rem; margin-bottom: 2rem; }
        .csp-back-btn { background: none; border: 1px solid rgba(255,255,255,0.2); color: #94a3b8; padding: 0.4rem 0.8rem; border-radius: 0.5rem; cursor: pointer; }
        .csp-breadcrumb { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #64748b; }
        .csp-breadcrumb .active { color: #a78bfa; font-weight: 600; }
        .csp-breadcrumb .done { color: #34d399; }
        .csp-bc-sep { color: #475569; }
        .csp-error-banner { background: rgba(239,68,68,0.15); border: 1px solid rgba(239,68,68,0.4); padding: 0.75rem 1rem; border-radius: 0.5rem; margin-bottom: 1rem; color: #fca5a5; }
        .csp-step { max-width: 900px; margin: 0 auto; }
        .csp-step h2 { font-size: 1.75rem; margin-bottom: 0.5rem; }
        .csp-subtitle { color: #94a3b8; margin-bottom: 2rem; }
        .csp-char-grid, .csp-spec-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 2rem; }
        .csp-char-card {
          background: rgba(255,255,255,0.05); border: 2px solid rgba(255,255,255,0.1);
          border-radius: 1rem; padding: 1.5rem 1rem; cursor: pointer;
          display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
          transition: all 0.2s; position: relative;
        }
        .csp-char-card:hover { border-color: #6366f1; background: rgba(99,102,241,0.1); }
        .csp-char-card.selected { border-color: #a78bfa; background: rgba(99,102,241,0.15); }
        .csp-char-card.new-char { border-style: dashed; color: #6366f1; }
        .csp-char-icon { font-size: 2rem; }
        .csp-char-name { font-weight: 600; text-align: center; font-size: 0.9rem; }
        .csp-char-class { font-size: 0.7rem; color: #64748b; background: rgba(255,255,255,0.08); padding: 0.15rem 0.5rem; border-radius: 9999px; }
        .csp-char-level { font-size: 0.75rem; color: #34d399; }
        .csp-selected-tick { position: absolute; top: 0.5rem; right: 0.75rem; color: #a78bfa; font-weight: 700; font-size: 1rem; }
        .csp-spec-card {
          background: rgba(255,255,255,0.05); border: 2px solid rgba(255,255,255,0.1);
          border-radius: 0.75rem; padding: 1.25rem; cursor: pointer;
          transition: all 0.2s; position: relative;
        }
        .csp-spec-card:hover:not(.locked) { border-color: #6366f1; }
        .csp-spec-card.selected { border-color: #a78bfa; background: rgba(99,102,241,0.15); }
        .csp-spec-card.locked { opacity: 0.5; cursor: not-allowed; }
        .csp-spec-name { font-weight: 600; margin-bottom: 0.25rem; }
        .csp-locked-badge { font-size: 0.75rem; color: #94a3b8; }
        .csp-new-char-form {
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.75rem; padding: 1.25rem; margin-bottom: 1.5rem;
          display: flex; flex-direction: column; gap: 0.75rem; max-width: 400px;
        }
        .csp-new-char-form h4 { margin: 0; font-size: 1rem; color: #e2e8f0; }
        .csp-new-char-form select, .csp-new-char-form input {
          padding: 0.5rem 0.75rem; border: 1px solid rgba(255,255,255,0.15);
          border-radius: 0.5rem; background: rgba(255,255,255,0.05); color: #e2e8f0;
          font-size: 0.9rem;
        }
        .csp-new-char-form button { padding: 0.5rem 1rem; background: #6366f1; color: #fff; border: none; border-radius: 0.5rem; cursor: pointer; }
        .csp-error { color: #fca5a5; font-size: 0.85rem; }
        .csp-continue-btn {
          padding: 0.75rem 2rem; background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: #fff; border: none; border-radius: 0.75rem; cursor: pointer;
          font-size: 1rem; font-weight: 700; transition: opacity 0.2s;
        }
        .csp-continue-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .csp-step2-actions { display: flex; justify-content: space-between; }
        .csp-back-step-btn { padding: 0.6rem 1.25rem; background: none; border: 1px solid rgba(255,255,255,0.2); color: #94a3b8; border-radius: 0.5rem; cursor: pointer; }
      `}</style>
    </div>
  );
}
